import { useEffect, useRef } from 'react'
import { APP_VERSION, fetchDeployedVersion, isNewerVersion, isVersionWatchEnabled } from './version'

// how often to poll the deploy marker. a minute is frequent enough to catch a
// deploy soon after it lands without adding meaningful load.
export const VERSION_POLL_INTERVAL_MS = 60_000

// watch for a newer deployed frontend and invoke `onUpdate` once — on an interval
// and whenever the tab regains focus (so a backgrounded tab is checked the moment
// the user returns to it). self-disables when the bundle has no real stamp (local
// dev), and fires `onUpdate` at most once per mount so the prompt is not repeated.
//
// `enabled` lets the caller stand the poller down: where a service worker registers, a
// waiting worker is the better deploy signal and this is the fallback for everywhere else.
export const useVersionWatcher = (
  onUpdate: () => void,
  { enabled = true }: { enabled?: boolean } = {}
): void => {
  // hold the latest callback without re-subscribing the listeners each render
  const onUpdateRef = useRef(onUpdate)
  onUpdateRef.current = onUpdate

  useEffect(() => {
    if (!enabled || !isVersionWatchEnabled) return

    let notified = false
    const controller = new AbortController()

    const check = async () => {
      if (notified) return
      const deployed = await fetchDeployedVersion(controller.signal)
      if (notified || controller.signal.aborted) return
      if (isNewerVersion(APP_VERSION, deployed)) {
        notified = true
        onUpdateRef.current()
      }
    }

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void check()
    }

    const interval = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibility)
    // check once on mount so a tab opened just after a deploy updates promptly
    void check()

    return () => {
      controller.abort()
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled])
}
